import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Endocrine subsection adapter
 * Handles endocrine system assessment and menstrual cycle tracking (5 fields)
 */
export class EndocrineAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Endocrine assessment fields
    const endocrineWnl = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_WNL
    )?.at(0)

    const menstrualCycle = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_CYCLE
    )?.at(0)

    const menstrualDate = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_DATE
    )?.at(0)

    const endocrineNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [SocFormField.ENDOCRINE_WNL]: endocrineWnl,
      [SocFormField.ENDOCRINE_MENSTRUAL_CYCLE]: menstrualCycle,
      [SocFormField.ENDOCRINE_MENSTRUAL_DATE]: menstrualDate,
      [SocFormField.ENDOCRINE_NOTES]: endocrineNotes,
      [SocFormField.ENDOCRINE_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_WNL,
        value: this.getFormDataField(SocFormField.ENDOCRINE_WNL)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_SYSTEM_SUMMARY,
        value: this.getFormDataField(SocFormField.ENDOCRINE_SYSTEM_SUMMARY)
      }
    ]

    // Conditionally add menstrual cycle fields if they exist (they are conditionally rendered)
    if (this.formDataFieldExists(SocFormField.ENDOCRINE_MENSTRUAL_CYCLE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_CYCLE,
        value: this.getFormDataField(SocFormField.ENDOCRINE_MENSTRUAL_CYCLE)
      })
    }

    if (this.formDataFieldExists(SocFormField.ENDOCRINE_MENSTRUAL_DATE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_DATE,
        value: this.getFormDataField(SocFormField.ENDOCRINE_MENSTRUAL_DATE)
      })
    }

    if (this.formDataFieldExists(SocFormField.ENDOCRINE_NOTES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_NOTES,
        value: this.getFormDataField(SocFormField.ENDOCRINE_NOTES)
      })
    }

    return mutations
  }
}
